import { createLearnerClient, indexedDbStorage, type LearnerClient } from "@tutor/live";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthGate, type LearnerSession } from "./auth";
import { LocaleProvider, useT } from "./i18n";
import { LearnerClientContext } from "./learner-context";
import { LessonScreen } from "./lesson/LessonScreen";
import { ParentEntry } from "./parent/ParentMode";
import { DisplaySettings } from "./settings/DisplaySettings";
import { useLearnerView } from "./useLearnerView";
import type { VoiceAccess } from "./voice/access";

export function App() {
  return (
    <AuthGate>
      {(session, actions) => (
        <LocaleProvider locale={session.locale}>
          <LearnerApp session={session} actions={actions} />
        </LocaleProvider>
      )}
    </AuthGate>
  );
}

/** Same-origin live URL: the learner Worker forwards `/v1/*` to the API (ADR-0022). */
export function liveUrl(profileId: string, since: number): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/v1/learners/${encodeURIComponent(profileId)}/live?since=${String(since)}`;
}

function LearnerApp(props: { session: LearnerSession; actions: { lock(): void } }) {
  const { session, actions } = props;
  const onLock = useCallback(() => {
    actions.lock();
  }, [actions]);
  const { profileId, token, voiceEntitled } = session;
  const [client, setClient] = useState<LearnerClient | null>(null);
  // The token the current client was opened with; a newer one is passed on, not reopened.
  const opened = useRef<string | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    document.documentElement.lang = session.locale;
  }, [session.locale]);

  useEffect(() => {
    const created = createLearnerClient({
      auth: { profileId, token: tokenRef.current },
      url: liveUrl,
      storage: indexedDbStorage(),
    });
    opened.current = tokenRef.current;
    setClient(created);
    return () => {
      created.close();
      setClient(null);
    };
    // A new client per learner only; token changes go through `reauthenticate` below.
  }, [profileId]);

  useEffect(() => {
    if (client !== null && opened.current !== token) {
      opened.current = token;
      client.reauthenticate({ profileId, token });
    }
  }, [client, profileId, token]);

  // Voice mode only when the API entitled this household; it reads the latest token.
  const voice = useMemo<VoiceAccess | null>(
    () => (voiceEntitled ? { profileId, getToken: () => Promise.resolve(tokenRef.current) } : null),
    [voiceEntitled, profileId],
  );

  if (client === null) return <Splash />;
  return (
    <LearnerClientContext.Provider value={client}>
      <Lesson client={client} onLock={onLock} voice={voice} />
      {/* Parent mode (workstream 7) opens on top; the lesson stays mounted underneath. */}
      <ParentEntry />
    </LearnerClientContext.Provider>
  );
}

function Lesson(props: { client: LearnerClient; onLock: () => void; voice: VoiceAccess | null }) {
  const view = useLearnerView(props.client);
  return (
    <>
      <LessonScreen view={view} client={props.client} onLock={props.onLock} voice={props.voice} />
      <DisplaySettings />
    </>
  );
}

function Splash() {
  const t = useT();
  return (
    <div className="splash" role="status">
      {t("app.loading")}
    </div>
  );
}
